import { ChangeEvent, useState } from "react";
import {
  Checkbox,
  Dialog,
  DialogContent,
  FormControlLabel,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import DialogActions from "@mui/material/DialogActions";
import LoadingSpinner from "./LoadingSpinner";
import GetAppIcon from "@mui/icons-material/GetApp";
import { useAppSelector } from "redux/hooks";
import { selectClientLensExportConfigs } from "redux/slices/clientLens";
import { selectCollectEnabled } from "redux/slices/session";
import { useExportFunctions } from "helpers/hooks/useExportFunctions";
import { GetCasesParamsInput } from "generated/graphql";
import { pluralize } from "helpers/coll";

export function BulkExportModal({
  lensId,
  open,
  setOpen,
  casesTotal,
  caseFilters,
}: {
  lensId: number;
  open: boolean;
  setOpen: (open: boolean) => void;
  casesTotal: number;
  caseFilters: GetCasesParamsInput;
}) {
  const collectEnabled = useAppSelector(selectCollectEnabled);

  const [exporting, setExporting] = useState(false);
  const [configId, setConfigId] = useState<number>(-1);
  const [shareToCollect, setShareToCollect] = useState(false);
  const [pushToTransfer, setPushToTransfer] = useState(false);
  const [customTransferFolder, setCustomTransferFolder] = useState<string>("");
  const profileExportConfigs = useAppSelector(selectClientLensExportConfigs);
  const { doBulkCaseExport } = useExportFunctions();

  const handleExport = async () => {
    setExporting(true);

    try {
      const configName = profileExportConfigs.find((pec) => pec.id === configId)?.name;
      await doBulkCaseExport({
        lensId,
        caseFilters,
        collectShared: shareToCollect,
        pushToTransfer,
        customTransferFolder: customTransferFolder || undefined,
        configName,
      });
      setOpen(false);
    } finally {
      setExporting(false);
    }
  };

  const handleCancel = () => {
    setOpen(false);
  };

  return (
    <Dialog open={open} onClose={handleCancel} fullWidth maxWidth="sm">
      <DialogContent>
        <p className="font-bold">
          Generate Exports for {casesTotal.toLocaleString()} {pluralize(casesTotal, "Case")}
        </p>
        <FormControl sx={{ width: "300px", mt: 2 }}>
          <InputLabel>Config</InputLabel>
          <Select
            value={configId}
            size="small"
            label="Config"
            onChange={(event) => setConfigId(event.target.value as number)}
            displayEmpty>
            <MenuItem value={-1}>Standard</MenuItem>
            {profileExportConfigs.map((pec) => (
              <MenuItem key={pec.id} value={pec.id}>
                {pec.name}
              </MenuItem>
            ))}
          </Select>

          <FormControlLabel
            label="Share to Collect"
            disabled={!collectEnabled}
            control={<Checkbox checked={shareToCollect} onChange={(evt) => setShareToCollect(evt.target.checked)} />}
          />
          <FormControlLabel
            label="Send to SFTP"
            control={<Checkbox checked={pushToTransfer} onChange={(evt) => setPushToTransfer(evt.target.checked)} />}
          />
          {pushToTransfer && (
            <TextField
              id="custom-transfer-folder"
              label="Custom Transfer Folder"
              value={customTransferFolder}
              onChange={(event: ChangeEvent<any>) => {
                setCustomTransferFolder(event.target.value);
              }}
            />
          )}
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color="default">
          Cancel
        </Button>
        <Button
          onClick={handleExport}
          variant="outlined"
          color="default"
          disabled={exporting}
          startIcon={exporting ? <LoadingSpinner size={24} /> : <GetAppIcon />}>
          Export
        </Button>
      </DialogActions>
    </Dialog>
  );
}
